import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import {
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  UPCOMING_TASKS_QUERY_KEY,
} from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";
import { addTeamFiltersToQueryOptions } from "@coworker/apprestructured/src/TasksOverview/helpers/addTeamFiltersToQueryOptions";
import { getTasksEndpointUrl } from "@coworker/apprestructured/src/TasksOverview/helpers/getTasksEndpointUrl";
import { TaskListItem } from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { httpClient } from "@coworker/http-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

type TaskQueryResult = {
  list: TaskListItem[];
};

type ActiveTaskState = "open" | "in_progress" | "pending";

type TaskRequestOptions = {
  state: ActiveTaskState;
  sortedBy: string;
  includeUpcoming: boolean;
  assignedTo: string;
};

const getSortedBy = (isUpcoming: boolean) =>
  isUpcoming ? "startAsc" : "createdAtDesc";

const fetchAllTasksV2 = async (
  teamId: string,
  state: ActiveTaskState,
  isUpcoming: boolean
) => {
  const queryOptions: TaskRequestOptions = {
    state,
    assignedTo: teamId,
    sortedBy: getSortedBy(isUpcoming),
    includeUpcoming: isUpcoming,
  };

  await addTeamFiltersToQueryOptions(teamId, queryOptions, "", teamId);

  const url = `/v2/${getTasksEndpointUrl(queryOptions)}`;
  const result = await httpClient.tasks.get<{ data: TaskQueryResult }>(url);

  return result.data;
};

const fetchAllTasksV1 = async (
  teamId: string,
  state: ActiveTaskState,
  isUpcoming: boolean
) => {
  const queryOptions: TaskRequestOptions = {
    state,
    assignedTo: teamId,
    sortedBy: getSortedBy(isUpcoming),
    includeUpcoming: isUpcoming,
  };

  await addTeamFiltersToQueryOptions(teamId, queryOptions, "", teamId);

  const url = `/v1/${getTasksEndpointUrl(queryOptions)}`;
  const result = await httpClient.tasks.get<{ data: TaskQueryResult }>(url);

  return result.data;
};

type FetchAllTasksByStateArgs = {
  teamId: string;
  state: ActiveTaskState;
  isUpcoming: boolean;
  isKeysetEnabled: boolean;
};
const fetchAllTasksByState = async ({
  teamId,
  state,
  isUpcoming,
  isKeysetEnabled,
}: FetchAllTasksByStateArgs) => {
  if (isKeysetEnabled) {
    return fetchAllTasksV2(teamId, state, isUpcoming);
  }

  return fetchAllTasksV1(teamId, state, isUpcoming);
};

const useQueries = (teamId: string, enabled: boolean = true) => {
  const isKeysetEnabled = useFlag(FLAGS.ENABLE_KEY_SET_GET_TASKS);

  const defaultQueryOptions = {
    staleTime: 0,
    refetchOnWindowFocus: true,
    enabled,
  };

  const openQuery = useQuery({
    ...defaultQueryOptions,
    queryKey: [
      OPEN_TASKS_QUERY_KEY,
      "assignedTeamActive",
      teamId,
      isKeysetEnabled,
    ],
    queryFn: () =>
      fetchAllTasksByState({
        teamId,
        state: "open",
        isUpcoming: false,
        isKeysetEnabled,
      }),
  });

  const inProgressQuery = useQuery({
    ...defaultQueryOptions,
    queryKey: [
      IN_PROGRESS_TASKS_QUERY_KEY,
      "assignedTeamActive",
      teamId,
      isKeysetEnabled,
    ],
    queryFn: () =>
      fetchAllTasksByState({
        teamId,
        state: "in_progress",
        isUpcoming: false,
        isKeysetEnabled,
      }),
  });

  const upcomingQuery = useQuery({
    ...defaultQueryOptions,
    queryKey: [
      UPCOMING_TASKS_QUERY_KEY,
      "assignedTeamActive",
      teamId,
      isKeysetEnabled,
    ],
    queryFn: () =>
      fetchAllTasksByState({
        teamId,
        state: "pending",
        isUpcoming: true,
        isKeysetEnabled,
      }),
  });

  return { openQuery, inProgressQuery, upcomingQuery };
};

export const useAssignedTeamActiveTasks = (
  teamId: string,
  enabled: boolean = true
) => {
  const { openQuery, inProgressQuery, upcomingQuery } = useQueries(
    teamId,
    enabled
  );

  const isLoading =
    openQuery.isLoading || inProgressQuery.isLoading || upcomingQuery.isLoading;
  const error = openQuery.error || inProgressQuery.error || upcomingQuery.error;

  const { tasksCount, tasks } = useMemo(() => {
    if (error) {
      return { tasksCount: 0, tasks: [] };
    }

    const openTasks = openQuery.data?.list || [];
    const inProgressTasks = inProgressQuery.data?.list || [];
    const upcomingTasks = upcomingQuery.data?.list || [];

    const tasks = [...openTasks, ...inProgressTasks, ...upcomingTasks];

    return { tasksCount: tasks.length, tasks };
  }, [error, openQuery.data, inProgressQuery.data, upcomingQuery.data]);

  return { tasksCount, tasks, isLoading, error };
};
